// src/pages/Dashboard.tsx
import React, { useMemo } from 'react';
import { useUser } from "@clerk/clerk-react";
import { useTenantNavigate } from '../services/hooks';
import { 
    Users, 
    UserCheck, 
    UserX, 
    Pause,
    Calendar,
    Layers,
    BarChart3,
    CalendarDays,
    CircleDollarSign,
    UserCog,
    FileCheck
} from "lucide-react";

// Importar componentes
import MetricCard from '../components/dashboard/MetricCard';
import QuickAccessCard from '../components/dashboard/QuickAccessCard';
import UpcomingEvents from '../components/dashboard/UpcomingEvents';
import WelcomeHeader from '../components/dashboard/WelcomeHeader';
import SystemAlert from '../components/dashboard/SystemAlert';
import ClientesEnPausa from '../components/dashboard/ClientesEnPausa';
import PagosPendientes from '../components/dashboard/PagosPendientes';

// Importar servicios
import { useClientsWithPlazasQuery } from "../services/hooks/useClientes";
import { useFestivosQuery } from "../services/hooks/useFestivos";
import { useEventosQuery } from "../services/hooks/useEventos";
import { usePagosQuery } from '../services/hooks/usePagos';
import { formatDate } from "../utils/utils";
import { Cliente } from '../types/clientes';

interface ClientePausa {
    id: string;
    title: string;
    date: string;
    reason: string;
    proximoAVolver: boolean;
    fechaVencida?: boolean; 
}

const Dashboard: React.FC = () => {
    const { user } = useUser();
    const navigate = useTenantNavigate();
    const { data: clients } = useClientsWithPlazasQuery();
    const { data: festivos } = useFestivosQuery();
    const { data: eventos } = useEventosQuery();
    const { data: pagos, isLoading: isLoadingPagos } = usePagosQuery();
    const { data: allClients } = useClientsWithPlazasQuery(); // Para obtener los nombres de los clientes
    
    // Métricas de clientes
    const clientStats = useMemo(() => {
        if (!clients) return { 
            total: 0, 
            active: 0, 
            inactive: 0, 
            enEspera: 0,
            withPlazas: 0, 
            totalPlazas: 0,
            contratoFirmado: 0
        };
        
        const active = clients.filter((c: Cliente) => c.activo).length;
        const inactive = clients.length - active;
        const enEspera = clients.filter((c: Cliente) => c.enEspera === true).length;
        const withPlazas = clients.filter((c: Cliente) => c.plazas && c.plazas.length > 0).length;
        const totalPlazas = clients.reduce((sum: number, client: Cliente) => sum + (client.plazas?.length || 0), 0);
        const contratoFirmado = clients.filter((c: Cliente) => c.contratoActivo?.estado === 'firmado').length;
        
        return {
            total: clients.length,
            active,
            inactive,
            enEspera,
            withPlazas,
            totalPlazas,
            contratoFirmado
        };
    }, [clients]);

    // Clientes con plazas en pausa (en espera)
    const clientesEnPausa = useMemo(() => {
        if (!clients) return { todos: [], proximosAVolver: [] };
        
        const hoy = new Date();
        const enDosSemana = new Date();
        enDosSemana.setDate(hoy.getDate() + 14); // Próximos 14 días

        // Todos los clientes en pausa
        const todosEnPausa = clients
            .filter((client: Cliente) => 
                client.enEspera && client.fechaFinEspera
            )
            .sort((a: Cliente, b: Cliente) => 
                new Date(a.fechaFinEspera!).getTime() - new Date(b.fechaFinEspera!).getTime()
            )
            .map((client: Cliente) => {
                const fechaFin = new Date(client.fechaFinEspera!);
                const hoy = new Date();
                hoy.setHours(0, 0, 0, 0); // Normalizar a la medianoche para comparar solo fechas
                
                return {
                    id: client._id,
                    title: `${client.nombre}`,
                    date: `Vuelve el ${formatDate(client.fechaFinEspera!)}`,
                    reason: client.motivoEspera || 'No especificado',
                    proximoAVolver: fechaFin <= enDosSemana && fechaFin >= hoy,
                    fechaVencida: fechaFin < hoy // True si la fecha de fin es anterior a hoy
                };
            });

        // Clientes próximos a volver (en la próxima semana)
        const proximosAVolver = todosEnPausa
            .filter((cliente: ClientePausa) => cliente.proximoAVolver)
            .slice(0, 5); // Limitamos a 5 para la vista rápida
        
        return {
            todos: todosEnPausa,
            proximosAVolver
        };
    }, [clients]);

    // Próximos festivos
    const proximosFestivos = useMemo(() => {
        if (!festivos) return [];
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return festivos
        .filter(festivo => new Date(festivo.fecha) >= today)
        .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
        .slice(0, 5)
        .map(festivo => ({
            title: festivo.descripcion || 'Día festivo',
            date: formatDate(festivo.fecha)
        }));
    }, [festivos]);

    // Próximos eventos (clases)
    const proximosEventos = useMemo(() => {
        if (!eventos) return [];
        
        const today = new Date();
        
        return eventos
        .filter(evento => 
            !evento.cancelado && 
            new Date(evento.fecha) >= today
        )
        .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
        .slice(0, 5)
        .map(evento => ({
            title: `${evento.nivel?.nombre || 'Clase'} - ${evento.horaInicio}`,
            date: formatDate(evento.fecha)
        }));
    }, [eventos]);

    // Pagos pendientes del mes actual
    const pagosPendientesMes = useMemo(() => {
        if (!pagos || !allClients) return [];
        
        const mesActual = new Date().getMonth() + 1; // Los meses en JS son 0-indexed, pero en tu modelo son 1-indexed
        const anioActual = new Date().getFullYear();
        
        return pagos
            .filter(pago => 
                pago.estado === 'Abierto' && 
                pago.mes === mesActual && 
                pago.anio === anioActual
            )
            .map(pago => {
                // Buscar el cliente asociado a esta suscripción
                const clienteId = typeof pago.suscripcion_id === 'string' 
                    ? pago.suscripcion_id 
                    : pago.suscripcion_id._id;
                    
                // Encontrar el cliente que tiene esta suscripción
                const cliente = allClients.find((c: Cliente) => 
                    c.suscripciones && c.suscripciones.some(s => s._id === clienteId)
                );
                
                // Crear fecha de vencimiento (último día del mes)
                const ultimoDiaMes = new Date(pago.anio, pago.mes, 0).getDate();
                const fechaVencimiento = `${ultimoDiaMes}/${pago.mes}/${pago.anio}`;
                
                return {
                    id: pago._id || '',
                    clienteNombre: cliente ? cliente.nombre : 'Cliente desconocido',
                    suscripcion_id: typeof pago.suscripcion_id === 'string' ? pago.suscripcion_id : pago.suscripcion_id._id,
                    importe: pago.importe,
                    metodoPago: pago.metodoPago,
                    fechaVencimiento
                };
            })
            .sort((a, b) => a.clienteNombre.localeCompare(b.clienteNombre));
    }, [pagos, allClients]);

    return (
        <div className="flex flex-col w-full bg-gray-50">
            {/* El header está fuera de este componente, así que comenzamos desde el contenido */}
            <div className="flex-1 pt-16">
                <main className="px-4 py-6 mx-auto w-full max-w-7xl mb-16">
                    {/* Mensaje de bienvenida */}
                    <WelcomeHeader 
                        userName={user?.firstName || user?.username || 'Usuario'}
                        onClientClick={() => navigate('/clientes')}
                        onCalendarClick={() => navigate('/calendario')}
                    />
                    
                    {/* Fila de métricas */}
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Métricas de negocio</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-7 gap-4 mb-6">
                        <MetricCard 
                            icon={<Users size={24} />}
                            title="Total Clientes"
                            value={clientStats.total}
                            bgColor="bg-white"
                            textColor="text-blue-600"
                        />
                        <MetricCard 
                            icon={<UserCheck size={24} />}
                            title="Clientes Activos"
                            value={clientStats.active}
                            bgColor="bg-white"
                            textColor="text-green-600"
                        />
                        <MetricCard 
                            icon={<UserX size={24} />}
                            title="Clientes Inactivos"
                            value={clientStats.inactive}
                            bgColor="bg-white"
                            textColor="text-red-600"
                        />
                        <MetricCard 
                            icon={<Pause size={24} />}
                            title="En Pausa"
                            value={clientStats.enEspera}
                            bgColor="bg-white"
                            textColor="text-yellow-600"
                        />
                        <MetricCard 
                            icon={<Layers size={24} />}
                            title="Con Plazas"
                            value={clientStats.withPlazas}
                            bgColor="bg-white"
                            textColor="text-purple-600"
                        />
                        <MetricCard 
                            icon={<BarChart3 size={24} />}
                            title="Total Plazas"
                            value={clientStats.totalPlazas}
                            bgColor="bg-white"
                            textColor="text-indigo-600"
                        />
                        {/* Contratos aún no está disponible: se anuncia en vez de
                            mostrar un 0 que parece un dato real. */}
                        <MetricCard
                            icon={<FileCheck size={24} />}
                            title="Contratos Firmados"
                            comingSoon
                            bgColor="bg-white"
                            textColor="text-teal-600"
                        />
                    </div>
                    
                    {/* Accesos rápidos */}
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Accesos rápidos</h2>
                    {user?.publicMetadata.role === 'admin' ? (
                        // Grid completo para superadmin (4 columnas)
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <QuickAccessCard 
                                icon={<Users size={20} />}
                                title="Clientes"
                                description="Gestiona tus clientes y sus datos"
                                onClick={() => navigate('/clientes')}
                                bgColor="bg-blue-600"
                            />
                            <QuickAccessCard 
                                icon={<CalendarDays size={20} />}
                                title="Calendario"
                                description="Visualiza y gestiona tus clases"
                                onClick={() => navigate('/calendario')}
                                bgColor="bg-green-600"
                            />
                            <QuickAccessCard 
                                icon={<CircleDollarSign size={20} />}
                                title="Facturación"
                                description="Gestiona pagos y domiciliaciones"
                                onClick={() => navigate('/clientes/sepa')}
                                bgColor="bg-purple-600"
                            />
                            <QuickAccessCard 
                                icon={<UserCog size={20} />}
                                title="Configuraciones"
                                description="Ajustes y configuraciones del sistema"
                                onClick={() => navigate('/configuraciones')}
                                bgColor="bg-gray-700"
                            />
                        </div>
                    ) : (
                        // Grid centrado para usuarios normales (2 columnas)
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-2xl mx-auto">
                            <QuickAccessCard 
                                icon={<Users size={20} />}
                                title="Clientes"
                                description="Gestiona tus clientes y sus datos"
                                onClick={() => navigate('/clientes')}
                                bgColor="bg-blue-600"
                            />
                            <QuickAccessCard 
                                icon={<CalendarDays size={20} />}
                                title="Calendario"
                                description="Visualiza y gestiona tus clases"
                                onClick={() => navigate('/calendario')}
                                bgColor="bg-green-600"
                            />
                        </div>
                    )}
                    
                    {/* Tablero de información */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        <ClientesEnPausa 
                            todosEnPausa={clientesEnPausa.todos} 
                            proximosAVolver={[]}
                        />
                        <UpcomingEvents 
                            title="Próximos festivos" 
                            items={proximosFestivos} 
                        />
                        <PagosPendientes 
                            pagos={pagosPendientesMes}
                            isLoading={isLoadingPagos}
                        />
                    </div>
                    
                    {/* Zona para novedades o avisos */}
                    <SystemAlert
                        title="Avisos del sistema"
                        message="Recuerda que puedes gestionar los festivos del año desde el calendario principal."
                        severity="warning"
                    />
                </main>
            </div>
        </div>
    );
};

export default Dashboard;


// import { useUser, UserButton } from "@clerk/clerk-react";
// import MainCalendar from "../components/calendario/MainCalendar";

// const Dashboard = () => {
//     const { user } = useUser();
//     return (
//         <div className="flex flex-col w-full h-screen ">
//             <div className="bg-white p-4 shadow-md">
//                 <div className="flex flex-col items-center space-x-4">
//                     <span><UserButton/></span>
//                     <span className="text-sm text-center m-0">{user?.firstName || user?.username} </span>                    
//                 </div>
//             </div>
//             <main className="flex-1 h-screen p-4  mx-auto">
//                 <div className="w-full pb-24">
//                 {/* <h2 className="text-3xl text-center font-bold mb-4">Bienvenido, {user?.firstName}</h2>
//                 <p className="text-lg">Puedes visitar la opción de Clientes para comenzar a trabajar.</p> */}
//                     <MainCalendar />
//                 </div>
//             </main>
//         </div>
//     );
// };

// export default Dashboard;
